import { useEffect, useCallback, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { $getRoot, $insertNodes, EditorState, FORMAT_TEXT_COMMAND, FORMAT_ELEMENT_COMMAND, UNDO_COMMAND, REDO_COMMAND } from 'lexical';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list';
import { LinkNode } from '@lexical/link';

interface RichEditorProps {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  minHeight?: number;
}

// 初始化时将 HTML 注入编辑器
function HtmlInitPlugin({ value }: { value: string }) {
  const [editor] = useLexicalComposerContext();
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current || !value) return;
    initialized.current = true;
    editor.update(() => {
      const parser = new DOMParser();
      const dom = parser.parseFromString(value, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      const root = $getRoot();
      root.clear();
      $insertNodes(nodes);
    });
  }, [editor, value]);

  return null;
}

// 工具栏组件
function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();

  const formatText = useCallback((format: 'bold' | 'italic' | 'underline' | 'strikethrough') => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  }, [editor]);

  const formatAlign = useCallback((align: 'left' | 'center' | 'right' | 'justify') => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, align);
  }, [editor]);

  const insertList = useCallback((type: 'bullet' | 'number') => {
    if (type === 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  }, [editor]);

  const insertImage = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const url = ev.target?.result as string;
        editor.update(() => {
          const root = $getRoot();
          const sel = root.selectEnd();
          if (sel) {
            const imgHtml = `<img src="${url}" alt="${file.name}" style="max-width:100%" />`;
            const parser = new DOMParser();
            const dom = parser.parseFromString(imgHtml, 'text/html');
            const nodes = $generateNodesFromDOM(editor, dom);
            $insertNodes(nodes);
          }
        });
      };
      reader.readAsDataURL(file);
    };
    input.click();
  }, [editor]);

  const btnClass = 'px-2 py-1 rounded hover:bg-gray-200 text-gray-600 text-sm transition-colors cursor-pointer';
  const divider = <div className="w-px h-5 bg-gray-300 mx-1" />;

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50">
      <button type="button" className={btnClass} onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)} title="撤销">
        <i className="ri-arrow-go-back-line" />
      </button>
      <button type="button" className={btnClass} onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)} title="重做">
        <i className="ri-arrow-go-forward-line" />
      </button>
      {divider}
      <button type="button" className={btnClass} onClick={() => formatText('bold')} title="加粗">
        <i className="ri-bold" />
      </button>
      <button type="button" className={btnClass} onClick={() => formatText('italic')} title="斜体">
        <i className="ri-italic" />
      </button>
      <button type="button" className={btnClass} onClick={() => formatText('underline')} title="下划线">
        <i className="ri-underline" />
      </button>
      <button type="button" className={btnClass} onClick={() => formatText('strikethrough')} title="删除线">
        <i className="ri-strikethrough" />
      </button>
      {divider}
      <button type="button" className={btnClass} onClick={() => formatAlign('left')} title="左对齐">
        <i className="ri-align-left" />
      </button>
      <button type="button" className={btnClass} onClick={() => formatAlign('center')} title="居中">
        <i className="ri-align-center" />
      </button>
      <button type="button" className={btnClass} onClick={() => formatAlign('right')} title="右对齐">
        <i className="ri-align-right" />
      </button>
      <button type="button" className={btnClass} onClick={() => formatAlign('justify')} title="两端对齐">
        <i className="ri-align-justify" />
      </button>
      {divider}
      <button type="button" className={btnClass} onClick={() => insertList('bullet')} title="无序列表">
        <i className="ri-list-unordered" />
      </button>
      <button type="button" className={btnClass} onClick={() => insertList('number')} title="有序列表">
        <i className="ri-list-ordered" />
      </button>
      {divider}
      <button type="button" className={btnClass} onClick={insertImage} title="插入图片">
        <i className="ri-image-line" />
      </button>
    </div>
  );
}

const theme = {
  text: {
    bold: 'font-bold',
    italic: 'italic',
    underline: 'underline',
    strikethrough: 'line-through',
  },
  list: {
    ul: 'list-disc pl-6 my-2',
    ol: 'list-decimal pl-6 my-2',
    listitem: 'my-1',
  },
  quote: 'border-l-4 border-gray-300 pl-4 italic text-gray-500 my-2',
  heading: {
    h1: 'text-3xl font-bold my-3',
    h2: 'text-2xl font-bold my-2',
    h3: 'text-xl font-bold my-2',
  },
  link: 'text-teal-600 underline cursor-pointer',
};

export default function RichEditor({
  value,
  onChange,
  placeholder = '请输入正文内容...',
  minHeight = 380,
}: RichEditorProps) {
  const initialConfig = {
    namespace: 'RichEditor',
    theme,
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode],
    onError: (error: Error) => console.error(error),
  };

  const handleChange = useCallback(
    (editorState: EditorState, editor: Parameters<typeof $generateHtmlFromNodes>[0]) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor, null);
        onChange(html);
      });
    },
    [onChange]
  );

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarPlugin />
        <div className="relative" style={{ minHeight }}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="outline-none px-4 py-3 text-sm text-gray-700 leading-relaxed"
                style={{ minHeight }}
              />
            }
            placeholder={
              <div className="absolute top-3 left-4 text-gray-400 text-sm pointer-events-none select-none">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <OnChangePlugin onChange={handleChange} />
          <HtmlInitPlugin value={value} />
        </div>
      </LexicalComposer>
    </div>
  );
}
